/** @jsxImportSource @emotion/react */
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { AppBar, AppBarProps, Box, Button, Grid, Theme, Typography, useTheme } from '@mui/material';
import AuthDropdown from 'components/AuthDropdown';
import LangsDropdown from 'components/LangsDropdown';
import { usePaths } from 'hooks/usePaths';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { UseStyles } from 'types/styles';
import { asset } from 'utils/asset';
import { UserCountries } from './UserCountries';
import UserIcon from 'components/icons/UserIcon';
import { AppContext } from 'App';

const Header: React.FC<AppBarProps> = ({ ...rest }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const paths = usePaths();
  const navigate = useNavigate();
  const { setIsAccessRequestOpen } = useContext(AppContext);

  const handleOpenDialog = () => {
    setIsAccessRequestOpen?.(true);
  };

  return (
    <AppBar css={classes.Header} position="static" {...rest}>
      <Box>
        <Grid container spacing={4} css={classes.toolbar}>
          <Grid item xs="auto">
            <Link to={paths.home.generate()}>
              <img src={asset('/images/logo.svg')} alt="" width={155} height={50} css={classes.logo} />
            </Link>
          </Grid>
          <Grid item xs="auto">
            <Typography mb={-1} variant="body2" component="div" color={theme.palette.custom.primary.brand[50]}>
              <strong>
                <FormattedMessage id={'Header.title'} defaultMessage={'Partners Platform'} />
              </strong>
            </Typography>
            <Typography variant="subtitle1" component="div">
              <strong>
                <FormattedMessage
                  id={'Header.subTitle'}
                  defaultMessage={'Health Emergency Preparedness, Response and Resilience (HEPR)'}
                />
              </strong>
            </Typography>
          </Grid>
          <Grid item xs sx={{ flexGrow: 1 }}>
            <UserCountries />
          </Grid>
          <Grid item xs="auto">
            <LangsDropdown />
          </Grid>
          <AuthenticatedTemplate>
            <Grid item xs="auto">
              <AuthDropdown />
            </Grid>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <Grid item xs="auto">
              <Button onClick={handleOpenDialog} variant="outlined" css={classes.accessBtn}>
                <FormattedMessage id={'Header.requestAccess'} defaultMessage={'Request access'} />
              </Button>
            </Grid>
            <Grid item xs="auto">
              <Button
                onClick={() => navigate(paths.signIn.generate())}
                variant="contained"
                color="primary"
                endIcon={<UserIcon css={classes.userIcon} />}
              >
                <FormattedMessage id={'Header.logIn'} defaultMessage={'Log in'} />
              </Button>
            </Grid>
          </UnauthenticatedTemplate>
        </Grid>
      </Box>
    </AppBar>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  Header: {
    padding: `${theme.spacing(5)} ${theme.spacing(6)}`,
    boxShadow: 'none',
    borderBottom: `10px solid ${theme.palette.custom.primary.brand[100]}`,
  },
  logo: {
    display: 'block',
  },
  toolbar: {
    alignItems: 'center',
  },
  accessBtn: {
    color: theme.palette.common.white,
    background: 'none',

    '&:hover, &:focus': {
      backgroundColor: theme.palette.custom.primary.brand[120],
      borderColor: theme.palette.custom.primary.brand[110],
    },
    '&:active': {
      backgroundColor: theme.palette.custom.primary.brand[110],
      borderColor: theme.palette.custom.primary.brand[100],
    },
  },
  userIcon: {
    fontSize: '18px !important',
  },
});

export default Header;
